import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ChevronUpIcon, MapLayerIcon } from '@rsos/assets/icons';
import { Box } from '@rsos/base-components';
import { PURE_WIRELESS_CAPABILITY } from '@rsos/capstone/src/constants/capabilities';
import { IRP_MAP_ENABLED } from '@rsos/capstone/src/constants/permissionNames';
import BasemapTooltip from '@rsos/components/capstone/BasemapMenu/BasemapTooltip';
import Popover from '@rsos/components/capstone/base/Popover';
import {
  allSpatialLayersSelector,
  filteredDynamicLayers,
} from '@rsos/components/capstone/common/Map/SpatialLayers/selectors';
import { isSpatialLayerDynamic } from '@rsos/components/capstone/common/Map/utils/spatialLayersHelpers';
import { CONNECTED_SITES_CATEGORY } from '@rsos/constants/dataTypeCategories';
import { useFlag } from '@rsos/flags';
import useHasCapability from '@rsos/hooks/useHasCapability';
import { GOOGLE } from '@rsos/maps/constants/mapProviders';
import {
  gisLayerFor,
  gisSublayerFor,
  GISServiceTypes,
  loadGISLayer,
  selectGISLayers,
  updateLayer,
} from '@rsos/reduxDataStore';
import {
  selectAdditionalLayersCount,
  clearAllAdditionalLayers,
  setPureWirelessVisible,
} from '@rsos/reduxDataStore/src/features/additionalLayers/additionalLayersSlice';
import { selectUserPermissionsByApp } from '@rsos/sinatra';
import { useAppDispatch, useAppSelector } from '@rsos/utils/reduxHooks';
import {
  clearCheckedLayers,
  toggleSpatialLayer,
  toggleSpatialLayerGroup,
} from '../../actions/spatialLayers';
import { useEsriAccess } from '../../hooks/useEsriAccess';
import { AdditionalLayersControls } from './AdditionalLayersControls/AdditionalLayersControls';
import { GISLayerControls } from './GISLayerControls/GISLayerControls';
import LayerCheckbox from './LayerCheckbox';
import { RegularCheckboxWrapper } from './LayerCheckbox.styles';
import LayerCheckboxGroup from './LayerCheckboxGroup';
import {
  Controls,
  AccordionTitleWrapper,
  AccordionTitle,
  CollapsedIconWrapper,
  AccordionContent,
  ControlsBottom,
  SelectedLayersWrapper,
  SelectedText,
  ClearAllText,
} from './MapLayersControl.styles';
import { MapLayersTabs } from './MapLayersTabs';
import { INTELLIGENCE_TAB, SUPPLEMENTAL_LAYER_CATEGORIES } from './constants';
import { getGroupedlayers } from './helpers';

const KNOWN_CATEGORIES = new Set(
  SUPPLEMENTAL_LAYER_CATEGORIES.map(c => c.value),
);

// Resolve the tab/category a GIS layer belongs to. Layers without a known
// supplemental_layer_category are grouped under the Intelligence tab.
const getLayerCategory = layer => {
  const category = layer.service.attributes?.supplemental_layer_category;
  if (category && KNOWN_CATEGORIES.has(category)) {
    return category;
  }
  return INTELLIGENCE_TAB.value;
};

const isExcludedGISLayer = layer =>
  layer.service.service_type === GISServiceTypes.basemap ||
  layer.service.service_type === GISServiceTypes.geocode;

export const MapLayersControlWithTabs = () => {
  const dispatch = useAppDispatch();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isGmrLandingZoneEnabled = useFlag('gmr_landing_zone');

  const permissions = useAppSelector(state =>
    selectUserPermissionsByApp(state.sinatra.user, 'capstone'),
  );

  const hasEsriMapAccess = useEsriAccess();
  const { irp_access_token } = useAppSelector(state => state.sinatra.irp);
  const isIRPmode =
    permissions && permissions.includes(IRP_MAP_ENABLED) && irp_access_token;

  const gisLayers = useSelector(selectGISLayers);
  const gisServicesPresent = gisLayers.length > 0;
  const numAdditionalLayers = useSelector(selectAdditionalLayersCount);
  const shouldRenderGISControls =
    hasEsriMapAccess && gisServicesPresent && (!irp_access_token || isIRPmode);

  const mapProvider = useAppSelector(state => state.sinatra.ui.mapProvider);

  const allSpatialLayers = useAppSelector(state =>
    allSpatialLayersSelector(state.spatialLayers),
  );
  const dynamicLayers = useAppSelector(
    state => state.spatialLayers.dynamicLayers,
  );
  const disabledDynamicLayers = useAppSelector(
    state => state.spatialLayers.disabledDynamicLayers,
  );
  const shouldDisplayDynamicLayers = !!filteredDynamicLayers(
    dynamicLayers,
    disabledDynamicLayers,
  ).length;

  const isLayerDisabled = useMemo(() => mapProvider === GOOGLE, [mapProvider]);

  const [hoveredLayerId, setHoveredLayerId] = useState(null);
  const hasPureWirelessCapability = useHasCapability(
    PURE_WIRELESS_CAPABILITY,
  ).hasCapability;

  const isPureWirelessLayerEnabled =
    useFlag('pure_wireless_layer') && hasPureWirelessCapability;

  const [activeTab, setActiveTab] = useState(INTELLIGENCE_TAB.value);
  const [hasInitializedTab, setHasInitializedTab] = useState(false);

  const availableTabs = useMemo(() => {
    const nonExcluded = gisLayers.filter(
      l =>
        l.service.service_type !== GISServiceTypes.basemap &&
        l.service.service_type !== GISServiceTypes.geocode,
    );
    const specificValues = SUPPLEMENTAL_LAYER_CATEGORIES.filter(
      c => c.value !== INTELLIGENCE_TAB.value,
    ).map(c => c.value);
    const specificCategoriesInUse = new Set(
      nonExcluded
        .map(l => l.service.attributes.supplemental_layer_category)
        .filter(cat => cat && specificValues.includes(cat)),
    );
    if (specificCategoriesInUse.size === 0) {
      return [];
    }
    const presentCategories = SUPPLEMENTAL_LAYER_CATEGORIES.filter(
      cat =>
        cat.value === INTELLIGENCE_TAB.value ||
        specificCategoriesInUse.has(cat.value),
    );
    return presentCategories;
  }, [gisLayers]);

  useEffect(() => {
    if (availableTabs.length === 0) return;
    if (!hasInitializedTab) {
      setActiveTab(availableTabs[0].value);
      setHasInitializedTab(true);
    } else if (!availableTabs.some(t => t.value === activeTab)) {
      setActiveTab(availableTabs[0].value);
    }
  }, [availableTabs, activeTab, hasInitializedTab]);

  const isIntelligenceTab = activeTab === INTELLIGENCE_TAB.value;

  const onHandleHoverLayer = layerID => setHoveredLayerId(layerID);

  const shouldRenderMLControls = !irp_access_token || isIRPmode;

  const toggleAccordion = () => {
    setIsCollapsed(prev => !prev);
  };

  // GIS layers belonging to the currently active tab (excluding basemap/geocode).
  const gisLayersInActiveTab = useMemo(
    () =>
      gisLayers.filter(
        l => !isExcludedGISLayer(l) && getLayerCategory(l) === activeTab,
      ),
    [gisLayers, activeTab],
  );

  const numActiveGISServicesInTab = useMemo(
    () => gisLayersInActiveTab.filter(l => l.active).length,
    [gisLayersInActiveTab],
  );

  const [layerCheckboxes, setLayerCheckboxes] = useState([]);
  const [groupedLayerCheckboxes, setGroupedLayerCheckboxes] = useState({});

  // Layers that only render inside the Intelligence tab (additional layers
  // and static/dynamic spatial layers) only contribute to the count when the
  // Intelligence tab is active.
  const intelligenceTabExtrasCount = isIntelligenceTab
    ? numAdditionalLayers +
      layerCheckboxes.reduce((count, cb) => {
        if (cb?.layer?.isChecked) {
          return count + 1;
        }
        return count;
      }, 0) +
      Object.values(groupedLayerCheckboxes).reduce((count, cb) => {
        if (cb?.layers?.find(i => i.layer.isChecked)) {
          return count + 1;
        }
        return count;
      }, 0)
    : 0;

  const selectedCheckboxCount =
    numActiveGISServicesInTab + intelligenceTabExtrasCount;

  const handleClearAll = useCallback(() => {
    if (hasEsriMapAccess) {
      gisLayersInActiveTab.forEach(layer => {
        if (!layer.active) return;
        const mapLayer = gisLayerFor(layer);
        if (mapLayer) {
          mapLayer.visible = false;
        }
        dispatch(updateLayer({ layerID: layer.id, active: false }));
      });
    }
    if (isIntelligenceTab) {
      dispatch(clearCheckedLayers());
      dispatch(clearAllAdditionalLayers());
    }
  }, [hasEsriMapAccess, dispatch, gisLayersInActiveTab, isIntelligenceTab]);

  const handleSelectAll = useCallback(() => {
    if (hasEsriMapAccess) {
      gisLayersInActiveTab.forEach(layer => {
        if (layer.active) return;

        // Mirror the behavior of GISLayerControl#toggle: load the layer
        // on-demand when it hasn't been loaded yet, then flip visibility
        // on both the service and its valid sublayers before syncing Redux.
        if (!layer.loaded && !layer.loading) {
          dispatch(loadGISLayer({ service: layer.service }))
            .unwrap()
            .then(result => {
              const { loadedLayer } = result;
              const gisLayer = gisLayerFor(loadedLayer);
              if (gisLayer) {
                gisLayer.visible = true;
                const excludedIds = loadedLayer.filteredSublayerIds || [];
                loadedLayer.sublayers.forEach(sl => {
                  const gisSublayer = gisSublayerFor(sl);
                  if (!gisSublayer) return;
                  gisSublayer.visible = !excludedIds.includes(sl.uniqueId);
                });
              }
              dispatch(updateLayer({ layerID: layer.id, active: true }));
            })
            .catch(error => {
              // eslint-disable-next-line no-console
              console.error('Failed to load layer:', layer.title, error);
            });
          return;
        }

        const gisLayer = gisLayerFor(layer);
        if (!gisLayer) return;

        const filteredIds = layer.filteredSublayerIds || [];
        layer.sublayers.forEach(sl => {
          const gisSublayer = gisSublayerFor(sl);
          if (!gisSublayer) return;
          gisSublayer.visible = !filteredIds.includes(sl.uniqueId);
        });
        gisLayer.visible = true;

        dispatch(updateLayer({ layerID: layer.id, active: true }));
      });
    }
    if (isIntelligenceTab) {
      if (isPureWirelessLayerEnabled) {
        dispatch(setPureWirelessVisible(true));
      }
      layerCheckboxes.forEach(cb => {
        if (cb?.layer?.isChecked) return;
        const isDynamic = isSpatialLayerDynamic(cb.layer);
        dispatch(toggleSpatialLayer(cb.key, isDynamic, true));
      });
      Object.values(groupedLayerCheckboxes).forEach(group => {
        const uncheckedKeys = (group.layers || [])
          .filter(i => !i.layer.isChecked)
          .map(i => i.key);
        if (uncheckedKeys.length > 0) {
          dispatch(toggleSpatialLayerGroup(uncheckedKeys));
        }
      });
    }
  }, [
    hasEsriMapAccess,
    dispatch,
    gisLayersInActiveTab,
    isIntelligenceTab,
    isPureWirelessLayerEnabled,
    layerCheckboxes,
    groupedLayerCheckboxes,
  ]);

  const hasAnySelectedInTab = selectedCheckboxCount > 0;

  useEffect(() => {
    const filterPlaceholders = layer => {
      return (
        layer.key !== 'avive_aed_placeholder' &&
        layer.key !== 'avive_aed_highlight' &&
        layer.category !== CONNECTED_SITES_CATEGORY &&
        (layer.key !== 'gmr_landing_zones' || isGmrLandingZoneEnabled)
      );
    };

    const spatialLayers = allSpatialLayers.map(layer => ({
      id: layer.key,
      key: layer.key,
      category: layer.category?.key,
      layer,
      isGISSupplementalLayer: false,
      isDisabled: false,
    }));
    const groupedLayers = getGroupedlayers(spatialLayers);
    setGroupedLayerCheckboxes(groupedLayers);

    setLayerCheckboxes([...spatialLayers.filter(filterPlaceholders)]);
  }, [allSpatialLayers, mapProvider, isLayerDisabled, isGmrLandingZoneEnabled]);

  if (
    !(
      shouldRenderMLControls &&
      (shouldRenderGISControls ||
        shouldDisplayDynamicLayers ||
        layerCheckboxes.length > 0 ||
        Object.keys(groupedLayerCheckboxes).length > 0 ||
        isPureWirelessLayerEnabled)
    )
  ) {
    return null;
  }

  return (
    <Controls>
      <AccordionTitleWrapper onClick={toggleAccordion} horizontal>
        <MapLayerIcon color="primaryText" width={16} height={18} />
        <AccordionTitle data-name="map-layers-control-title">
          Map Layers
        </AccordionTitle>
        <CollapsedIconWrapper isCollapsed={isCollapsed}>
          <ChevronUpIcon color="secondaryLink" width={16} height={12} />
        </CollapsedIconWrapper>
      </AccordionTitleWrapper>
      {!isCollapsed && (
        <>
          <MapLayersTabs
            tabs={availableTabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          <AccordionContent>
            {shouldRenderGISControls && (
              <GISLayerControls categoryFilter={activeTab} />
            )}

            {/* Additional Layers (Pure Wireless, etc.) */}
            {isIntelligenceTab && isPureWirelessLayerEnabled && (
              <AdditionalLayersControls />
            )}

            {/* Static / Dynamic spatial layers */}
            {isIntelligenceTab &&
              layerCheckboxes.map(cb => {
                return (
                  <div
                    key={cb.id}
                    onMouseEnter={() => onHandleHoverLayer(cb.id)}
                    onMouseLeave={() => onHandleHoverLayer(null)}
                  >
                    <Popover
                      cssDisplayProperty="block"
                      isOpen={cb?.isDisabled ? cb.id === hoveredLayerId : false}
                      popoverID={`map-layers-control-popover-${cb.id}`}
                      trigger={
                        <Box horizontal>
                          <Box grow>
                            <LayerCheckbox
                              id={cb.layer.id}
                              isDisabled={cb.isDisabled}
                              isGISSupplementalLayer={cb.isGISSupplementalLayer}
                              key={cb.key}
                              layer={cb.layer}
                            />
                          </Box>
                        </Box>
                      }
                      useCustomOffset
                      xOffset={0}
                      yOffset={24}
                    >
                      <BasemapTooltip />
                    </Popover>
                  </div>
                );
              })}
            {isIntelligenceTab &&
              Object.values(groupedLayerCheckboxes).map(group => (
                <RegularCheckboxWrapper key={group.id}>
                  <LayerCheckboxGroup group={group} />
                </RegularCheckboxWrapper>
              ))}
          </AccordionContent>
          <ControlsBottom>
            <SelectedLayersWrapper>
              <SelectedText data-name="num-selected-layers">
                {selectedCheckboxCount} Selected
              </SelectedText>
              <ClearAllText
                data-name={
                  hasAnySelectedInTab ? 'clear-all-layers' : 'select-all-layers'
                }
                onClick={hasAnySelectedInTab ? handleClearAll : handleSelectAll}
              >
                {hasAnySelectedInTab ? 'Clear All' : 'Select All'}
              </ClearAllText>
            </SelectedLayersWrapper>
          </ControlsBottom>
        </>
      )}
    </Controls>
  );
};
